import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { ArrowLeft, Video, KeyRound, Copy, CheckCircle2, AlertCircle, UserCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CUENTA_ZOOM = 'ibauruguay01@gmail.com';
const CLAVE_ZOOM = 'Estudiantes01z!';

export default function ClaseProfesorZoom() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const claseId = params.get('clase_id');

  const [clase, setClase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiado, setCopiado] = useState(null); // 'email' | 'clave' | null

  useEffect(() => {
    if (!claseId) {
      setError('Falta parámetro clase_id');
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/clases/${claseId}/en-vivo-info`);
        setClase(data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'No se pudo cargar la clase');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [claseId]);

  const copiar = async (valor, tipo) => {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(tipo);
      toast.success(`${tipo === 'email' ? 'Email' : 'Contraseña'} copiada`);
      setTimeout(() => setCopiado(null), 2000);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const abrirZoom = () => {
    if (!clase?.zoom_start_url) {
      toast.error('Esta clase no tiene URL de Zoom configurada');
      return;
    }
    window.open(clase.zoom_start_url, '_blank', 'noopener,noreferrer');
    toast.info('🖥️ Zoom abierto en nueva pestaña');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
        <div className="text-white text-center">
          <div className="animate-spin h-10 w-10 border-4 border-white border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-lg">Cargando datos de la clase...</p>
        </div>
      </div>
    );
  }

  if (error || !clase) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-red-950 to-slate-900 p-6">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No se pudo cargar</h2>
          <p className="text-gray-600 mb-6">{error || 'Clase no encontrada'}</p>
          <Button onClick={() => navigate('/app/dashboard')} className="bg-red-500 hover:bg-red-600 text-white">
            Volver al Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-4 md:p-8"
      data-testid="clase-profesor-zoom"
    >
      {/* Back button */}
      <div className="max-w-3xl mx-auto mb-4">
        <button
          onClick={() => navigate('/app/dashboard')}
          className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-sm"
          data-testid="btn-back-dashboard"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver al Dashboard
        </button>
      </div>

      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header con banner de la clase */}
        <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          {clase.banner_url ? (
            <img
              src={clase.banner_url}
              alt={clase.titulo}
              className="w-full h-44 md:h-56 object-cover"
            />
          ) : (
            <div className="w-full h-44 md:h-56 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center">
              <Video className="h-20 w-20 text-white/80" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-5">
            <span className="inline-flex items-center gap-2 bg-amber-400/20 text-amber-200 text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm border border-amber-400/30 w-fit mb-2">
              <UserCircle className="h-3 w-3" />
              Entrada de Anfitrión
            </span>
            <h1 className="text-2xl md:text-3xl font-black text-white drop-shadow-lg" data-testid="clase-titulo">
              {clase.titulo}
            </h1>
            {clase.profesor_nombre && (
              <p className="text-slate-200 text-sm mt-1 flex items-center gap-2">
                <i className="fas fa-chalkboard-teacher"></i>
                {clase.profesor_nombre}
              </p>
            )}
          </div>
        </div>

        {/* Instrucciones paso a paso */}
        <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-5 md:p-6 text-white shadow-xl">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
            <span className="text-2xl">📋</span>
            Pasos para entrar como anfitrión
          </h2>
          <ol className="space-y-3 text-sm md:text-base">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-400 text-slate-900 font-black flex items-center justify-center text-sm">1</span>
              <span className="pt-0.5">Click en <strong className="text-amber-300">"Abrir Zoom"</strong> abajo — se abre en pestaña nueva</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-400 text-slate-900 font-black flex items-center justify-center text-sm">2</span>
              <span className="pt-0.5">Zoom te pide login. Usá las <strong className="text-amber-300">credenciales de Anfitrión IBA</strong> que están más abajo (copialas con un click)</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-400 text-slate-900 font-black flex items-center justify-center text-sm">3</span>
              <span className="pt-0.5">Una vez logueado, la clase inicia automáticamente con privilegios de <strong className="text-amber-300">Anfitrión</strong>. ¡Ves a todos los alumnos en galería!</span>
            </li>
          </ol>
        </div>

        {/* Credenciales copiables */}
        <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-2 border-amber-400/40 rounded-2xl p-5 md:p-6 backdrop-blur-lg shadow-xl">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="h-5 w-5 text-amber-300" />
            <h3 className="text-lg font-bold text-white">Credenciales de Anfitrión</h3>
          </div>

          <div className="space-y-3">
            {/* Email */}
            <div className="bg-slate-900/60 rounded-xl p-3 flex items-center justify-between gap-2 border border-white/10">
              <div className="min-w-0">
                <p className="text-amber-200 text-[10px] uppercase tracking-widest font-semibold mb-0.5">Email</p>
                <p className="text-white font-mono text-sm md:text-base truncate" data-testid="cred-email">{CUENTA_ZOOM}</p>
              </div>
              <button
                onClick={() => copiar(CUENTA_ZOOM, 'email')}
                className="flex-shrink-0 bg-amber-400 hover:bg-amber-300 text-slate-900 px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1 transition-all"
                data-testid="btn-copiar-email"
              >
                {copiado === 'email' ? (
                  <><CheckCircle2 className="h-3.5 w-3.5" /> Copiado</>
                ) : (
                  <><Copy className="h-3.5 w-3.5" /> Copiar</>
                )}
              </button>
            </div>

            {/* Password */}
            <div className="bg-slate-900/60 rounded-xl p-3 flex items-center justify-between gap-2 border border-white/10">
              <div className="min-w-0">
                <p className="text-amber-200 text-[10px] uppercase tracking-widest font-semibold mb-0.5">Contraseña</p>
                <p className="text-white font-mono text-sm md:text-base truncate" data-testid="cred-clave">{CLAVE_ZOOM}</p>
              </div>
              <button
                onClick={() => copiar(CLAVE_ZOOM, 'clave')}
                className="flex-shrink-0 bg-amber-400 hover:bg-amber-300 text-slate-900 px-3 py-2 rounded-lg font-semibold text-xs flex items-center gap-1 transition-all"
                data-testid="btn-copiar-clave"
              >
                {copiado === 'clave' ? (
                  <><CheckCircle2 className="h-3.5 w-3.5" /> Copiado</>
                ) : (
                  <><Copy className="h-3.5 w-3.5" /> Copiar</>
                )}
              </button>
            </div>
          </div>

          <p className="text-amber-200/80 text-xs mt-3 italic">
            💡 Estas credenciales son de la cuenta master IBA Uruguay. Solo las usan los profesores/admin para entrar como anfitrión. Zoom las recuerda después del primer login.
          </p>
        </div>

        {/* Botones: elegir método de entrada */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button
            onClick={abrirZoom}
            className="h-20 md:h-24 text-base md:text-lg font-black bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 hover:from-blue-600 hover:via-indigo-600 hover:to-purple-700 text-white shadow-2xl hover:shadow-blue-500/40 transition-all hover:scale-[1.02] rounded-2xl flex-col gap-0.5 py-3"
            data-testid="btn-abrir-zoom"
          >
            <span className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              Abrir Zoom
            </span>
            <span className="text-[10px] md:text-xs font-normal opacity-90">
              Pestaña nueva · Galería completa · Requiere login master
            </span>
          </Button>

          <Button
            onClick={() => navigate(`/app/clase-en-vivo?clase_id=${clase.id}`)}
            className="h-20 md:h-24 text-base md:text-lg font-black bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 hover:from-emerald-600 hover:via-teal-600 hover:to-cyan-700 text-white shadow-2xl hover:shadow-emerald-500/40 transition-all hover:scale-[1.02] rounded-2xl flex-col gap-0.5 py-3"
            data-testid="btn-sdk-embebido"
          >
            <span className="flex items-center gap-2">
              <i className="fas fa-laptop-code text-lg"></i>
              Entrar con SDK Web
            </span>
            <span className="text-[10px] md:text-xs font-normal opacity-90">
              Embebido en IBA · Sin login · Galería limitada
            </span>
          </Button>
        </div>

        {/* Tip */}
        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 text-slate-300 text-xs space-y-1">
          <p>
            <strong className="text-amber-300">💡 ¿Cuál elegir?</strong>
          </p>
          <p>
            <strong className="text-blue-300">Zoom nativo</strong> — ves a TODOS los alumnos en galería simultáneamente, todas las features de Zoom, pero pide login la primera vez.
          </p>
          <p>
            <strong className="text-emerald-300">SDK Web</strong> — clase embebida en IBA Connect, cero login, pero solo ves al hablante activo (limitación del SDK Web).
          </p>
        </div>
      </div>
    </div>
  );
}
